import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatErr, fmtDateShort, fmtGBP } from "@/lib/api";
import { useAuth, ROLE_LABEL } from "@/lib/auth";
import { Pencil, FloppyDisk, Check, CaretDown, CaretUp, Wrench } from "@phosphor-icons/react";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";

const AVATAR = "https://images.unsplash.com/photo-1621905251918-48416bd8575a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjB0ZWNobmljaWFuJTIwbWFpbnRlbmFuY2V8ZW58MHx8fHwxNzgwMjQ5ODgxfDA&ixlib=rb-4.1.0&q=85";
const OPEN_STATUSES = ["submitted", "pending_approval", "approved", "assigned", "in_progress"];

export default function Technicians() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => http.get("/users").then(r => r.data) });
  const { data: skills = [] } = useQuery({ queryKey: ["skills"], queryFn: () => http.get("/skills").then(r => r.data) });
  const { data: allJobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: () => http.get("/jobs").then(r => r.data) });
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });
  const skillMap = Object.fromEntries(skills.map(s => [s.id, s.name]));
  const storeMap = Object.fromEntries(stores.map(s => [s.id, s.name]));

  const canEditSkills = ["admin", "general_manager", "maintenance_manager"].includes(user?.role);
  const techs = users.filter(u => ["maintenance_staff", "contractor"].includes(u.role));

  // jobs grouped by tech
  const techJobsMap = useMemo(() => {
    const m = {};
    for (const j of allJobs) {
      if (!j.assigned_to_id) continue;
      (m[j.assigned_to_id] = m[j.assigned_to_id] || []).push(j);
    }
    return m;
  }, [allJobs]);

  const [editing, setEditing] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const open = (t) => { setChosen(t.skill_ids || []); setEditing(t.id); setErr(""); };
  const toggle = (sid) => setChosen(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);
  const save = async () => {
    setBusy(true); setErr("");
    try {
      await http.patch(`/users/${editing}`, { skill_ids: chosen });
      qc.invalidateQueries({ queryKey: ["users"] });
      setEditing(null);
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="overline">Workforce</div>
      <h1 className="text-3xl font-heading font-semibold tracking-tight mb-6">Technicians & Contractors</h1>

      <div className="space-y-3">
        {techs.map(t => {
          const techJobs = techJobsMap[t.id] || [];
          const openJobs = techJobs.filter(j => OPEN_STATUSES.includes(j.status));
          const completed = techJobs.filter(j => j.status === "completed" || j.status === "closed").length;
          const isExpanded = !!expanded[t.id];
          return (
            <div key={t.id} data-testid={`technician-card-${t.id}`} className="rmms-card overflow-hidden">
              <div className="p-5 flex items-start gap-4">
                <img src={AVATAR} alt="" className="h-12 w-12 rounded-sm border border-slate-200 object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium">{t.name}</div>
                    <span className="text-[10px] font-heading uppercase tracking-wider text-slate-500">{ROLE_LABEL[t.role]}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {((t.skill_ids && t.skill_ids.length) ? t.skill_ids.map(sid => skillMap[sid]).filter(Boolean) : (t.skill_tags || [])).map(name =>
                      <span key={name} className="text-[11px] px-2 py-0.5 border border-slate-200 rounded-sm font-mono-data text-slate-600">{name}</span>
                    )}
                    {(!t.skill_ids?.length && !t.skill_tags?.length) && <span className="text-xs text-slate-400">No skills tagged.</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-center">
                      <div className={`text-2xl font-mono-data tabular-nums leading-none ${openJobs.length > 0 ? "text-[#002FA7]" : "text-slate-400"}`}>{openJobs.length}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">Pending</div>
                    </div>
                    <div className="text-center pl-3 border-l border-slate-200">
                      <div className="text-2xl font-mono-data tabular-nums text-slate-700 leading-none">{completed}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">Done</div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-1">
                    {canEditSkills && editing !== t.id && (
                      <button onClick={() => open(t)} data-testid={`tech-edit-skills-${t.id}`} className="h-8 px-2 text-xs border border-slate-200 rounded-sm flex items-center gap-1 hover:bg-slate-50"><Pencil size={11} /> Skills</button>
                    )}
                    <button onClick={() => setExpanded(s => ({ ...s, [t.id]: !s[t.id] }))} data-testid={`tech-expand-${t.id}`}
                      className="h-8 px-2 text-xs border border-slate-200 rounded-sm flex items-center gap-1 hover:bg-slate-50">
                      Jobs {isExpanded ? <CaretUp size={11} /> : <CaretDown size={11} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Skill edit row */}
              {editing === t.id && (
                <div className="border-t border-slate-200 px-5 py-4 bg-slate-50">
                  <div className="overline mb-2">Skills</div>
                  <div className="flex flex-wrap gap-1">
                    {skills.map(s => (
                      <button key={s.id} type="button" onClick={() => toggle(s.id)} data-testid={`tech-skill-toggle-${t.id}-${s.id}`}
                        className={`text-[11px] px-2 py-1 border rounded-sm font-mono-data transition-colors ${
                          chosen.includes(s.id) ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                        }`}>
                        {chosen.includes(s.id) && <Check size={10} className="inline -mt-0.5 mr-0.5" />}
                        {s.name}
                      </button>
                    ))}
                  </div>
                  {err && <div className="text-xs text-rose-600 mt-2">{err}</div>}
                  <div className="flex justify-end gap-1 mt-3">
                    <button onClick={() => setEditing(null)} className="h-8 px-3 text-xs border border-slate-200 rounded-sm bg-white">Cancel</button>
                    <button onClick={save} disabled={busy} data-testid={`tech-skill-save-${t.id}`} className="h-8 px-3 text-xs text-white rounded-sm inline-flex items-center gap-1" style={{ background: "#002FA7" }}>
                      <FloppyDisk size={12} weight="bold" /> Save
                    </button>
                  </div>
                </div>
              )}

              {/* Expanded jobs panel */}
              {isExpanded && (
                <div className="border-t border-slate-200">
                  {techJobs.length === 0 ? (
                    <div className="px-5 py-8 text-center text-sm text-slate-500" data-testid={`tech-no-jobs-${t.id}`}>No jobs assigned yet.</div>
                  ) : (
                    <>
                      {openJobs.length > 0 && (
                        <div>
                          <div className="px-5 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-heading uppercase tracking-wider text-slate-500 flex items-center gap-2"><Wrench size={11} /> Pending ({openJobs.length})</div>
                          <div className="divide-y divide-slate-100">
                            {openJobs.map(j => (
                              <Link to={`/jobs/${j.id}`} key={j.id} data-testid={`tech-job-${t.id}-${j.job_number}`} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center px-5 py-3 hover:bg-slate-50 transition-colors">
                                <div className="min-w-0">
                                  <div className="text-sm font-medium truncate">{j.title}</div>
                                  <div className="text-[11px] text-slate-500 font-mono-data">{j.job_number} · {storeMap[j.store_id] || ""} · Due {fmtDateShort(j.due_date)}</div>
                                </div>
                                <PriorityBadge priority={j.priority} />
                                <span className="font-mono-data text-sm tabular-nums">{fmtGBP(j.estimated_cost)}</span>
                                <StatusBadge status={j.status} />
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                      {techJobs.length > openJobs.length && (
                        <div>
                          <div className="px-5 py-2 bg-slate-50 border-b border-t border-slate-200 text-[11px] font-heading uppercase tracking-wider text-slate-500">Completed history ({techJobs.length - openJobs.length})</div>
                          <div className="divide-y divide-slate-100">
                            {techJobs.filter(j => !OPEN_STATUSES.includes(j.status)).slice(0, 5).map(j => (
                              <Link to={`/jobs/${j.id}`} key={j.id} data-testid={`tech-job-${t.id}-${j.job_number}`} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-5 py-2 hover:bg-slate-50 transition-colors">
                                <div className="min-w-0">
                                  <div className="text-sm truncate">{j.title}</div>
                                  <div className="text-[11px] text-slate-500 font-mono-data">{j.job_number}</div>
                                </div>
                                <span className="font-mono-data text-[12px] tabular-nums">{fmtGBP(j.actual_cost || 0)}</span>
                                <StatusBadge status={j.status} />
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {techs.length === 0 && <div className="text-sm text-slate-500">No technicians yet.</div>}
      </div>
    </div>
  );
}
